
import type {ApiWorkforceProfileRecord} from '../WorkforceProfiles/workforceProfileApiTypes';

import {mapRequiredLanguageProfile} from './mapRequiredLanguageProfile';
import type {
  WorkforceProfileVersionEvolution,
  WorkforceProfileVersionEvolutionScoreDelta,
  WorkforceProfileVersionEvolutionScoreDeltaKey,
  WorkforceProfileVersionEvolutionScoreDeltas,
  WorkforceProfileVersionEvolutionSkillRef,
} from '../../types/WorkforceProfileTypes';

const mapVersionEvolutionSkillRef = (raw: unknown): WorkforceProfileVersionEvolutionSkillRef | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const entry = raw as ApiWorkforceProfileRecord;
  const skill = mapRequiredLanguageProfile(entry.skill);

  if (!skill) {
    return null;
  }

  const section = String(entry.section ?? '').trim();
  if (!section) {
    return null;
  }

  return {skill, section};
};

const mapVersionEvolutionSkillRefs = (raw: unknown): WorkforceProfileVersionEvolutionSkillRef[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map(mapVersionEvolutionSkillRef)
    .filter((entry): entry is WorkforceProfileVersionEvolutionSkillRef => entry != null);
};

const mapVersionEvolutionScoreDelta = (raw: unknown): WorkforceProfileVersionEvolutionScoreDelta | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const entry = raw as ApiWorkforceProfileRecord;
  const toNumber = (value: unknown): number | null => {
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  return {
    delta: toNumber(entry.delta),
    current: toNumber(entry.current),
    previous: toNumber(entry.previous),
  };
};

const SCORE_DELTA_KEY_MAP: Record<string, WorkforceProfileVersionEvolutionScoreDeltaKey> = {
  alignmentScore: 'alignmentScore',
  culturalFitScore: 'culturalFitScore',
  knowledgeSkillsAlignment: 'knowledgeSkillsAlignment',
  responsibilitiesAlignment: 'responsibilitiesAlignment',
  skillsCompetenciesAlignment: 'skillsCompetenciesAlignment',
};

const mapVersionEvolutionScoreDeltas = (raw: unknown): WorkforceProfileVersionEvolutionScoreDeltas => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const deltas: WorkforceProfileVersionEvolutionScoreDeltas = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const mappedKey = SCORE_DELTA_KEY_MAP[key];
    if (!mappedKey) {
      return;
    }

    const delta = mapVersionEvolutionScoreDelta(value);
    if (delta) {
      deltas[mappedKey] = delta;
    }
  });

  return deltas;
};

export const mapVersionEvolution = (raw: unknown): WorkforceProfileVersionEvolution | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const evolution = raw as ApiWorkforceProfileRecord;
  const available = Boolean(evolution.available);
  const summary = Array.isArray(evolution.summary)
    ? (evolution.summary as unknown[]).map((line) => String(line).trim()).filter(Boolean)
    : [];

  if (!available && summary.length === 0) {
    return null;
  }

  return {
    available,
    summary,
    baselineVersion: evolution.baselineVersion != null ? Number(evolution.baselineVersion) : null,
    currentVersion: evolution.currentVersion != null ? Number(evolution.currentVersion) : null,
    scoreDeltas: mapVersionEvolutionScoreDeltas(evolution.scoreDeltas),
    newlyMetSkills: mapVersionEvolutionSkillRefs(evolution.newlyMetSkills),
    closedGaps: mapVersionEvolutionSkillRefs(evolution.closedGaps),
    newGaps: mapVersionEvolutionSkillRefs(evolution.newGaps),
    regressions: mapVersionEvolutionSkillRefs(evolution.regressions),
  };
};
